// MES / production board logic — ported from planner/renderer.js (renderMESPage & helpers).
// live execution tracking off the overview milestone dates: where every active vessel is on
// the floor, how long it's been there, alerts, priority dispatch, and finite-capacity planning.
// pure functions so the screen embeds no business logic.
import { assemblyPctNum } from '../logic/overview.js';
export const MES_STAGES = [
    { key: 'grp', label: 'GRP', enter: 'grpStart', target: 60 },
    { key: 'assembly', label: 'Assembly', enter: 'assemblyStart', target: 120 },
    { key: 'engine', label: 'Engine', enter: 'engineInstallDate', target: 25 },
    { key: 'startup', label: 'Start-up', enter: 'engineStartupDate', target: 14 },
    { key: 'seatrial', label: 'Sea Trial', enter: 'seaTrialDate', target: 14 },
    { key: 'pdi', label: 'PDI', enter: 'pdiDate', target: 14 },
];
// status columns that can be flagged "Hold".
const MES_HOLD_COLS = [
    ['anf', 'AnF'], ['wa', 'WA'], ['pol', 'POL'], ['uh', 'UH'], ['stickers', 'Stickers'],
    ['fw', 'FW'], ['frl', 'FRL'], ['pdi', 'PDI'], ['seaTrial', 'Sea Trial'],
    ['engineInstall', 'Eng. Install'], ['engineStartup', 'Eng. Start-up'],
];
// remaining production steps (status columns not yet "Yes"), in the order they're done on the floor.
// Sea Trial comes before PDI, as in MES_STAGES and the journey strip — mesNextOp walks this list, so
// the order decides which station a boat is actionable at.
export const MES_WORK = [
    ['engineInstall', 'Eng. Install'], ['engineStartup', 'Eng. Start-up'], ['wa', 'Wash'],
    ['pol', 'Polish'], ['uh', 'Upholstery'], ['stickers', 'Stickers'], ['seaTrial', 'Sea Trial'], ['pdi', 'PDI'],
];
const sv = (v, k) => String(v[k] ?? '').trim();
/** parse an overview ISO date (YYYY-MM-DD[…]) to a date, or null. */
export function parseOvDate(s) {
    const m = String(s ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : null;
}
export function mesHolds(v) {
    return MES_HOLD_COLS.filter(([k]) => sv(v, k).toLowerCase() === 'hold').map(([, l]) => l);
}
export function mesOverdue(v, now = new Date()) {
    const d = parseOvDate(v.deliveryDate);
    return !!d && d < now && !v.deliveryConfirmed;
}
// The physical work checkpoints that carry a real Yes/Hold status column, in pipeline order. These
// are the ground truth for where a boat actually is — unlike the milestone DATES, which are often
// planned (a boat can carry a filled, past PDI date while still on hold at Sea Trial). GRP and
// Assembly have no status column, so their progress is read from dates only.
const MES_STAGE_STATUS = [
    ['engine', 'engineInstall'], ['startup', 'engineStartup'], ['seatrial', 'seaTrial'], ['pdi', 'pdi'],
];
/**
 * The boat's current production stage. null = done/left.
 *
 * Stage is decided from the actual work status, not just the furthest planned milestone date: the
 * status columns (Engine Install → Start-up → Sea Trial → PDI) mark the first checkpoint not yet
 * finished, and that CAPS how far the date-based position can advance. So a boat still on hold at Sea
 * Trial can't be counted as "in PDI" just because a planned PDI date has passed. Dates still place the
 * early, status-less part of the line (Backlog → GRP → Assembly → Engine).
 */
export function mesStageOf(v, now = new Date()) {
    if (v.left || v.deliveryConfirmed)
        return null;
    // Cap: the first physical checkpoint not marked "Yes" — the boat can't be past it. If every
    // checkpoint is done, there's no cap (it may legitimately reach the last stage).
    let capIdx = MES_STAGES.length - 1;
    for (const [key, col] of MES_STAGE_STATUS) {
        if (sv(v, col).toLowerCase() !== 'yes') {
            capIdx = MES_STAGES.findIndex((s) => s.key === key);
            break;
        }
    }
    // Date-based furthest stage whose entry date has passed (as before) — needed for GRP/Assembly.
    let dateIdx = -1;
    MES_STAGES.forEach((st, i) => {
        const d = st.enter ? parseOvDate(v[st.enter]) : null;
        if (d && d <= now)
            dateIdx = i;
    });
    const idx = Math.min(dateIdx, capIdx);
    return idx >= 0 ? MES_STAGES[idx] : { key: 'backlog', label: 'Backlog', enter: null, target: 0 };
}
export function mesRemaining(v) {
    return MES_WORK.filter(([k]) => sv(v, k).toLowerCase() !== 'yes');
}
/** the next actionable operation = first incomplete step; "Hold" on it = blocked. */
export function mesNextOp(v) {
    for (const [k, label] of MES_WORK) {
        const s = sv(v, k).toLowerCase();
        if (s !== 'yes')
            return { key: k, label, blocked: s === 'hold' };
    }
    return null; // everything done → ready to deliver
}
/** transparent priority score (higher = do sooner) with human-readable reasons. */
export function mesPriority(v, now = new Date()) {
    let score = 0;
    const reasons = [];
    const del = parseOvDate(v.deliveryDate);
    const overdue = !!del && del < now && !v.deliveryConfirmed;
    const dueDays = del ? Math.round((+del - +now) / 86400000) : null;
    if (overdue) {
        score += 50;
        reasons.push('overdue');
    }
    else if (dueDays != null) {
        if (dueDays <= 7) {
            score += 30;
            reasons.push('due ' + dueDays + 'd');
        }
        else if (dueDays <= 14) {
            score += 20;
            reasons.push('due ' + dueDays + 'd');
        }
        else if (dueDays <= 30) {
            score += 10;
            reasons.push('due ' + dueDays + 'd');
        }
    }
    const st = mesStageOf(v, now);
    if (st && st.enter) {
        const en = parseOvDate(v[st.enter]);
        // days in stage, clamped at 0: the status cap in mesStageOf can hold a boat at a stage whose
        // (planned) entry date is still in the future, which would otherwise read as negative dwell.
        const dis = en ? Math.max(0, Math.round((+now - +en) / 86400000)) : 0;
        if (st.target && dis > st.target) {
            const over = dis - st.target;
            score += Math.min(22, 10 + over * 0.3);
            reasons.push(over + 'd over ' + st.label);
        }
    }
    const holds = mesHolds(v);
    if (holds.length) {
        score += holds.length * 8;
        reasons.push(holds.length + ' hold' + (holds.length > 1 ? 's' : ''));
    }
    const rem = mesRemaining(v).length;
    const pct = assemblyPctNum(v);
    if (pct >= 90 || rem <= 2) {
        score += 12;
        reasons.push('near done');
    }
    return { score: Math.round(score), reasons, dueDays, overdue, holds, rem };
}
// ── Finite-capacity, priority-dispatch planning ──────────────────────────────────
/** crew per boat: 2 people, 3 for big boats (≥400h total build). */
export const mesCrew = (hrsPerBoat) => (hrsPerBoat >= 400 ? 3 : 2);
/**
 * parallel crew scheduler. A team's people work several boats at once (2–3 per boat); boats run in
 * priority order; when a boat finishes, its crew frees up and the next queued boat starts. returns
 * each boat with working-day offsets from today + crew size.
 */
export function mesSchedule(boats, members) {
    const M = Math.max(1, members);
    const q = boats.map((b) => ({
        boat: b,
        rem: Math.max(0, b.hrs),
        crew: Math.min(mesCrew(b.hrsPerBoat || 0), M),
        startOff: null,
        readyOff: null,
    }));
    let day = 0;
    let free = M;
    const active = [];
    const fill = () => {
        for (const b of q) {
            if (b.startOff !== null)
                continue;
            if (b.rem <= 0) {
                b.startOff = day;
                b.readyOff = day;
                continue;
            }
            if (free >= b.crew) {
                b.startOff = day;
                free -= b.crew;
                active.push(b);
            }
        }
    };
    fill();
    let guard = 0;
    while (active.length && guard++ < 4000) {
        day++;
        for (const b of active)
            b.rem -= b.crew * 8 * 0.6; // a day of that crew's output
        for (let i = active.length - 1; i >= 0; i--) {
            if (active[i].rem <= 0.001) {
                active[i].readyOff = day;
                free += active[i].crew;
                active.splice(i, 1);
            }
        }
        fill();
    }
    // the loop only ends with work left when the guard trips (absurdly long plans, e.g. 1 person × 30
    // big boats). give every boat still unfinished — queued OR mid-build — a finite estimate from the
    // last simulated day, so no row is ever returned with a null start/ready (a null ready offset reads
    // downstream as "ready today" and the boat is never counted late).
    q.forEach((b) => {
        if (b.startOff === null)
            b.startOff = day;
        if (b.readyOff === null)
            b.readyOff = day + Math.max(1, Math.ceil(Math.max(0, b.rem) / (b.crew * 8 * 0.6 || 1)));
    });
    return q.map((b) => ({ boat: b.boat, crew: b.crew, startOff: b.startOff, readyOff: b.readyOff }));
}
// ── small formatting helpers ─────────────────────────────────────────────────────
export const fmtHrs = (n) => n >= 1000 ? Math.round(n / 100) / 10 + 'k' : String(Math.round(n));
/** add N working days (skip Sat/Sun) to a date. */
export function addWorkingDays(from, n) {
    const d = new Date(from);
    let left = Math.max(0, Math.round(n));
    while (left > 0) {
        d.setUTCDate(d.getUTCDate() + 1);
        const g = d.getUTCDay();
        if (g !== 0 && g !== 6)
            left--;
    }
    return d;
}
export const ddmmyyyy = (d) => d ? `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}` : '';
export const JOURNEY_STAGES = [
    ...MES_STAGES.map(({ key, label }) => ({ key, label })),
    { key: 'delivery', label: 'Delivery' },
];
// the status column that can put a stage itself on hold.
const STAGE_HOLD_COL = {
    engine: 'engineInstall', startup: 'engineStartup', seatrial: 'seaTrial', pdi: 'pdi',
};
/**
 * where a boat is on its build journey. Steps before the current stage are done; the current one is
 * `hold` when it (or any finishing column) is on hold, `late` when it has sat longer than its target
 * dwell, else `current`. Once PDI is signed off the boat is waiting on Delivery, which turns `late`
 * when the delivery date has passed. A delivered or departed boat is done all the way through.
 */
export function journeyOf(v, now = new Date()) {
    const steps = JOURNEY_STAGES.map((s) => ({ ...s, state: 'todo' }));
    if (v.left || v.deliveryConfirmed)
        return steps.map((s) => ({ ...s, state: 'done' }));
    const pdiDone = sv(v, 'pdi').toLowerCase() === 'yes';
    const st = mesStageOf(v, now);
    let cur = st ? JOURNEY_STAGES.findIndex((s) => s.key === st.key) : -1; // backlog → -1
    if (pdiDone)
        cur = JOURNEY_STAGES.length - 1; // PDI signed off → waiting on delivery
    if (cur < 0)
        return steps; // nothing started yet
    for (let i = 0; i < cur; i++)
        steps[i].state = 'done';
    const key = JOURNEY_STAGES[cur].key;
    const holdCol = STAGE_HOLD_COL[key];
    const onHold = (holdCol && sv(v, holdCol).toLowerCase() === 'hold') || mesHolds(v).length > 0;
    let late = false;
    if (key === 'delivery')
        late = mesOverdue(v, now);
    else if (st && st.enter && st.target) {
        const en = parseOvDate(v[st.enter]);
        late = !!en && Math.max(0, (+now - +en) / 86400000) > st.target; // future entry date ⇒ 0 days, never late
    }
    steps[cur].state = onHold ? 'hold' : late ? 'late' : 'current';
    // a delivery date that has already passed shows on the last step even while the boat is earlier on
    if (key !== 'delivery' && mesOverdue(v, now))
        steps[steps.length - 1].state = 'late';
    return steps;
}
//# sourceMappingURL=mes.js.map